import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import useLocales from "../../../hooks/useLocales";
import { axiosPrivate } from "../../../services/axios";
import EmployeeAPI from "../../../services/EmployeeService";
import { anchorOrigin } from "../../../utils/constants";
import { useSnackbar } from "notistack";
import {
  createDateForPicker,
  formatToYYYYMMDD
} from "../../../utils/customC2CFormat";
import { getDeleteEmployeeErrorMessage } from "../../../utils/placementErrroMessages";

export const DeleteEmployeeModal = ({
  onClose,
  employeeId,
  employeeList,
  setRefreshCount,
  row
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const [selectedDate, setSelectedDate] = useState(null);
  const [shouldDeleteSubmittedTs, setShouldDeleteSubmittedTs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [employeeError, setEmployeeError] = useState(null);

  const handleDelete = async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const payload = {
        end_date: formatToYYYYMMDD(selectedDate),
        delete_submitted: shouldDeleteSubmittedTs
      };
      await EmployeeAPI.DELETE(axiosPrivate, employeeId, payload);
      setRefreshCount((current) => current + 1);
      enqueueSnackbar("Employee deleted successfully", {
        variant: "success",
        anchorOrigin
      });
      onClose();
    } catch (error) {
      console.error("Error deleting employee:", error);
      setEmployeeError(error.response?.data || {});
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = (confirm) => {
    if (confirm) {
      setShouldDeleteSubmittedTs(true);
      handleDelete();
    } else {
      setShowErrorModal(false);
    }
  };

  const getErrorTitle = () => {
    switch (employeeError?.error_code) {
      case "DEL_001":
        return "Cannot delete as approved timesheets exist";
      case "DEL_002":
        return "Cannot delete as user has submitted timesheets";
      case "DEL_003":
        return "Please add valid end date";
      case "DEL_004":
        return "Please add valid end date";
      case "DEL_005":
        return "Please add valid end date";
      case "AUTH_D01":
        return "Only super admin can delete timesheets";
      default:
        return "This action cannot be completed. Please read the details and make the necessary changes accordingly.";
    }
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={onClose}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start"
          },
          "& .MuiPaper-root": {
            marginTop: "10vh"
          }
        }}
      >
        <DialogTitle>
          {translate("Select End Date To Delete This Employee")}
        </DialogTitle>
        <DialogContent>
          <DatePicker
            label="End Date"
            value={createDateForPicker(selectedDate)}
            onChange={(newValue) => setSelectedDate(newValue)}
            minDate={createDateForPicker(row?.start_date)}
            inputFormat="yyyy-MM-dd"
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                margin="dense"
                placeholder="yyyy-mm-dd"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined">
            {translate("Cancel")}
          </Button>
          <Button
            disabled={!selectedDate || loading}
            variant="contained"
            onClick={handleDelete}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {loading ? translate("Deleting...") : translate("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showErrorModal}
        onClose={() => handleConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
            color: "text.secondary",
            pb: 1
          }}
        >
          {getErrorTitle()}
        </DialogTitle>
        <DialogContent
          sx={{
            textAlign: "center",
            py: 2,
            px: 3,
            fontSize: "1rem",
            color: "text.primary"
          }}
        >
          {getDeleteEmployeeErrorMessage(employeeError?.error_code)}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
          {employeeError?.error_code === "DEL_002" ? (
            <>
              <Button
                onClick={() => handleConfirm(false)}
                color="secondary"
                variant="outlined"
                sx={{ px: 3, py: 1 }}
              >
                No
              </Button>
              <Button
                onClick={() => handleConfirm(true)}
                color="primary"
                variant="contained"
                sx={{ px: 3, py: 1 }}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                disabled={loading}
              >
                {loading ? "Deleting..." : "Yes"}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleConfirm(false)}
              color="primary"
              variant="contained"
              sx={{ px: 3, py: 1 }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
